import { ConfirmDialog } from '@pwa/components/confirm-dialog';
import { OrgInviteDialog } from '@pwa/components/dashboard/org-invite-dialog';
import { api } from '@pwa/lib/hono-client';
import { sessionQueryOptions } from '@pwa/queries/auth/session';
import { orgQueries } from '@pwa/queries/org/org';
import { Avatar, AvatarFallback, AvatarImage } from '@repo/ui/components/avatar';
import { Button } from '@repo/ui/components/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/components/card';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from '@repo/ui/components/dropdown-menu';
import { Skeleton } from '@repo/ui/components/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components/tooltip';
import { cn } from '@repo/ui/lib/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
	ArrowLeft,
	Mail,
	MoreVertical,
	Shield,
	ShieldAlert,
	Sparkles,
	Trash2,
	UserPlus,
	UsersRound,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_dashboard/org/$orgId/settings')({
	component: RouteComponent,
});

type Role = 'owner' | 'admin' | 'member';

interface ConfirmState {
	open: boolean;
	kind: 'remove-member' | 'cancel-invitation' | null;
	id: string | null;
	label: string;
}

const CLOSE_CONFIRM: ConfirmState = {
	open: false,
	kind: null,
	id: null,
	label: '',
};

function RouteComponent() {
	const { orgId } = Route.useParams();
	const queryClient = useQueryClient();
	const [isInviteOpen, setIsInviteOpen] = useState(false);
	const [confirmState, setConfirmState] = useState<ConfirmState>(CLOSE_CONFIRM);

	// Visual state for transitions/animations
	const [pendingRemovals, setPendingRemovals] = useState<ReadonlySet<string>>(new Set());
	const [flashMemberId, setFlashMemberId] = useState<string | null>(null);

	const { data: orgData, isLoading: isOrgLoading } = useQuery(orgQueries.detail(orgId));
	const { data: membersData, isLoading: isMembersLoading } = useQuery(orgQueries.members(orgId));
	const { data: invitationsData, isLoading: isInvitationsLoading } = useQuery(orgQueries.invitations(orgId));
	const { data: sessionData, isLoading: isSessionLoading } = useQuery(sessionQueryOptions);

	useEffect(() => {
		document.title = 'Organization settings · Scout';
	}, []);

	const triggerFlash = (memberId: string) => {
		setFlashMemberId(memberId);
	};

	const cancelInvitationMutation = useMutation({
		mutationFn: async (invitationId: string) => {
			const response = await api.orgs[':orgId'].invitations[':id'].cancel.$post({
				param: { orgId, id: invitationId },
			});
			const body = await response.json();
			if (!response.ok) {
				throw new Error('error' in body ? String(body.error) : 'Failed to cancel invitation');
			}
			return body;
		},
		onSuccess: () => {
			toast.success('Invite cancelled.');
			queryClient.invalidateQueries({
				queryKey: ['org', orgId, 'invitations'],
			});
		},
		onError: (err: Error) => {
			toast.error(err.message);
		},
	});

	const removeMemberMutation = useMutation({
		mutationFn: async (memberId: string) => {
			const response = await api.orgs[':orgId'].members[':id'].remove.$post({
				param: { orgId, id: memberId },
			});
			const body = await response.json();
			if (!response.ok) {
				throw new Error('error' in body ? String(body.error) : 'Failed to remove member');
			}
			return body;
		},
		onMutate: (memberId: string) => {
			// Mark the row as leaving so the height/opacity can transition smoothly before the data refetch.
			setPendingRemovals((prev) => {
				const next = new Set(prev);
				next.add(memberId);
				return next;
			});
		},
		onSuccess: () => {
			toast.success('Removed from org.');
			queryClient.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
		},
		onError: (err: Error) => {
			toast.error(err.message);
			// Clear the pending-removal flag so the row reappears if the request failed.
			setPendingRemovals(new Set());
		},
	});

	const updateRoleMutation = useMutation({
		mutationFn: async ({ memberId, role }: { memberId: string; role: Role }) => {
			const response = await api.orgs[':orgId'].members[':id'].role.$post({
				param: { orgId, id: memberId },
				json: { role },
			});
			const body = await response.json();
			if (!response.ok) {
				throw new Error('error' in body ? String(body.error) : 'Failed to update member role');
			}
			return body;
		},
		onSuccess: (_, variables) => {
			toast.success('Permission updated.');
			triggerFlash(variables.memberId);
			queryClient.invalidateQueries({ queryKey: ['org', orgId, 'members'] });
		},
		onError: (err: Error) => {
			toast.error(err.message);
		},
	});

	const isInitialLoading = isOrgLoading || isMembersLoading || isInvitationsLoading || isSessionLoading;

	const handleConfirm = () => {
		if (!confirmState.kind || !confirmState.id) {
			setConfirmState(CLOSE_CONFIRM);
			return;
		}
		if (confirmState.kind === 'remove-member') {
			removeMemberMutation.mutate(confirmState.id);
		} else if (confirmState.kind === 'cancel-invitation') {
			cancelInvitationMutation.mutate(confirmState.id);
		}
		setConfirmState(CLOSE_CONFIRM);
	};

	const confirmLoading = removeMemberMutation.isPending || cancelInvitationMutation.isPending;

	if (isInitialLoading) {
		return <OrganizationLoading />;
	}

	const organization = orgData?.organization;
	const members = membersData?.members ?? [];
	const pendingInvitations = invitationsData?.invitations ?? [];
	const user = sessionData?.data?.user;

	if (!organization) {
		return (
			<div className='container mx-auto max-w-4xl p-6'>
				<Card className='animate-in fade-in slide-in-from-bottom-2 duration-300'>
					<CardContent className='flex flex-col items-center justify-center p-12 space-y-4'>
						<p className='text-muted-foreground text-sm'>No organization found.</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	const currentMember = members.find((m) => m.userId === user?.id);
	const userRole = currentMember?.role;
	const isAtLeastAdmin = userRole === 'admin' || userRole === 'owner';
	const onlyYou = members.length <= 1;

	return (
		<div className='h-full flex-1 min-w-0 overflow-y-auto'>
			<div className='container mx-auto max-w-4xl p-6 space-y-8 pb-12 min-w-0'>
				{/* Back link */}
				<Link
					to='/org/$orgId'
					params={{ orgId }}
					className='group inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground'
					data-testid='org-back'
				>
					<ArrowLeft
						className='size-3 transition-transform duration-200 group-hover:-translate-x-0.5'
						aria-hidden='true'
					/>
					Back to dashboard
				</Link>

				<div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between'>
					<div>
						<h1 className='text-3xl font-bold tracking-tight'>Organization settings</h1>
						<p className='text-muted-foreground mt-1'>Manage organization members and invites.</p>
					</div>
					{isAtLeastAdmin ? (
						<Button className='gap-2 self-start sm:self-auto' onClick={() => setIsInviteOpen(true)}>
							<UserPlus className='size-4' aria-hidden='true' />
							Invite users
						</Button>
					) : (
						<Tooltip>
							<TooltipTrigger
								render={
									<span className='inline-flex cursor-not-allowed self-start sm:self-auto'>
										<Button className='gap-2' disabled>
											<UserPlus className='size-4' aria-hidden='true' />
											Invite users
										</Button>
									</span>
								}
							/>
							<TooltipContent side='bottom'>You need admin access to invite users to this organization.</TooltipContent>
						</Tooltip>
					)}
				</div>

				{/* Overview Section */}
				<Card className='shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 fill-mode-backwards'>
					<CardHeader>
						<CardTitle>About</CardTitle>
						<CardDescription>Basic info about your organization.</CardDescription>
					</CardHeader>
					<CardContent className='grid gap-4 sm:grid-cols-2'>
						<div className='space-y-1'>
							<span className='text-xs font-medium text-muted-foreground uppercase tracking-wider'>
								Organization name
							</span>
							<p className='text-lg font-semibold'>{organization.name}</p>
						</div>
						<div className='space-y-1'>
							<span className='text-xs font-medium text-muted-foreground uppercase tracking-wider'>
								Organization URL slug
							</span>
							<p className='text-lg font-semibold font-mono'>{organization.slug}</p>
						</div>
					</CardContent>
				</Card>

				{/* Members List */}
				<Card className='shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 delay-60 fill-mode-backwards'>
					<CardHeader>
						<div className='flex items-start justify-between gap-4'>
							<div>
								<CardTitle>Organization members</CardTitle>
								<CardDescription>See who&apos;s in the organization and what they can do.</CardDescription>
							</div>
							<div className='inline-flex items-center gap-1.5 rounded-full bg-muted/60 px-2.5 py-1 text-xs text-muted-foreground'>
								<UsersRound className='size-3' aria-hidden='true' />
								<span className='tabular-nums'>{members.length}</span>
							</div>
						</div>
					</CardHeader>
					<CardContent>
						{onlyYou ? (
							<div className='flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-center'>
								<div className='rounded-full bg-muted p-3 text-muted-foreground'>
									<UsersRound className='size-5' aria-hidden='true' />
								</div>
								<p className='text-sm font-medium'>It&apos;s just you right now</p>
								<p className='text-xs text-muted-foreground max-w-sm'>
									Invite members to collaborate on workspaces, share resources, and manage access together.
								</p>
							</div>
						) : (
							<div className='divide-y divide-border rounded-lg border overflow-hidden'>
								{members.map((m) => {
									const isSelf = m.userId === user?.id;
									const isTargetOwner = m.role === 'owner';
									const isRequesterAdmin = userRole === 'admin';
									const canManageMember = isAtLeastAdmin && !isSelf && !(isRequesterAdmin && isTargetOwner);
									const isRemoving = pendingRemovals.has(m.id);
									const isFlashing = flashMemberId === m.id;

									return (
										<div
											key={m.id}
											className={cn(
												'grid transition-all duration-300 ease-out',
												isRemoving ? 'grid-rows-[0fr] opacity-0' : 'grid-rows-[1fr] opacity-100',
											)}
										>
											<div className='overflow-hidden'>
												<div
													className={cn(
														'group/row flex items-center justify-between p-4 gap-4 flex-wrap sm:flex-nowrap',
														'transition-colors duration-150 hover:bg-muted/50',
													)}
												>
													<div className='flex items-center gap-3 min-w-0'>
														<Tooltip>
															<TooltipTrigger
																render={
																	<Avatar className='h-9 w-9 shrink-0 ring-1 ring-border transition-transform duration-200 group-hover/row:scale-105'>
																		<AvatarImage src={m.user.image ?? undefined} alt={m.user.name} />
																		<AvatarFallback className='bg-muted font-medium'>
																			{m.user.name.slice(0, 2).toUpperCase()}
																		</AvatarFallback>
																	</Avatar>
																}
															/>
															<TooltipContent side='top'>
																<p className='font-medium'>{m.user.name}</p>
																<p className='text-xs opacity-80'>{m.user.email}</p>
															</TooltipContent>
														</Tooltip>
														<div className='min-w-0'>
															<p className='text-sm font-medium leading-none truncate'>
																{m.user.name}&nbsp;
																{isSelf ? <span className='text-muted-foreground font-normal'>(You)</span> : null}
															</p>
															<p className='text-xs text-muted-foreground mt-1 truncate'>{m.user.email}</p>
														</div>
													</div>
													<div className='flex items-center gap-2 ml-auto sm:ml-0'>
														<span
															onAnimationEnd={(event) => {
																if (event.animationName === 'scout-role-flash-pulse') {
																	setFlashMemberId((current) => (current === m.id ? null : current));
																}
															}}
															className={cn(
																'relative inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium border shrink-0',
																'transition-all duration-300 ease-out',
																isFlashing && 'role-flash',
																m.role === 'owner' && 'bg-amber-500/10 text-amber-500 border-amber-500/20',
																m.role === 'admin' && 'bg-sky-500/10 text-sky-500 border-sky-500/20',
																m.role === 'member' && 'bg-muted text-muted-foreground border-border',
															)}
														>
															{isFlashing ? (
																<Sparkles
																	className='size-3 shrink-0 animate-in fade-in zoom-in-50 duration-300'
																	aria-hidden='true'
																/>
															) : m.role === 'owner' ? (
																<ShieldAlert className='size-3 shrink-0' aria-hidden='true' />
															) : m.role === 'admin' ? (
																<Shield className='size-3 shrink-0' aria-hidden='true' />
															) : null}
															<span>{m.role.charAt(0).toUpperCase() + m.role.slice(1)}</span>
														</span>

														{canManageMember ? (
															<DropdownMenu>
																<DropdownMenuTrigger
																	render={
																		<Button
																			variant='ghost'
																			className='h-8 w-8 p-0 shrink-0'
																			aria-label={`Manage ${m.user.name}`}
																		>
																			<MoreVertical className='size-4' aria-hidden='true' />
																		</Button>
																	}
																/>
																<DropdownMenuContent align='end'>
																	<DropdownMenuGroup>
																		<DropdownMenuLabel>Actions</DropdownMenuLabel>
																		<DropdownMenuSeparator />
																		<DropdownMenuSub>
																			<DropdownMenuSubTrigger>Change permission</DropdownMenuSubTrigger>
																			<DropdownMenuPortal>
																				<DropdownMenuSubContent>
																					<DropdownMenuItem
																						onClick={() =>
																							updateRoleMutation.mutate({
																								memberId: m.id,
																								role: 'member',
																							})
																						}
																						disabled={m.role === 'member'}
																					>
																						Member
																					</DropdownMenuItem>
																					<DropdownMenuItem
																						onClick={() =>
																							updateRoleMutation.mutate({
																								memberId: m.id,
																								role: 'admin',
																							})
																						}
																						disabled={m.role === 'admin'}
																					>
																						Admin
																					</DropdownMenuItem>
																					<DropdownMenuItem
																						onClick={() =>
																							updateRoleMutation.mutate({
																								memberId: m.id,
																								role: 'owner',
																							})
																						}
																						disabled={m.role === 'owner'}
																					>
																						Owner
																					</DropdownMenuItem>
																				</DropdownMenuSubContent>
																			</DropdownMenuPortal>
																		</DropdownMenuSub>
																		<DropdownMenuSeparator />
																		<DropdownMenuItem
																			variant='destructive'
																			onClick={() => {
																				setConfirmState({
																					open: true,
																					kind: 'remove-member',
																					id: m.id,
																					label: m.user.name,
																				});
																			}}
																			disabled={removeMemberMutation.isPending}
																		>
																			<Trash2 className='mr-2 size-4' aria-hidden='true' />
																			Remove from org
																		</DropdownMenuItem>
																	</DropdownMenuGroup>
																</DropdownMenuContent>
															</DropdownMenu>
														) : null}
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
						)}
					</CardContent>
				</Card>

				{/* Invitations List */}
				<Card className='shadow-sm animate-in fade-in slide-in-from-bottom-2 duration-300 delay-120 fill-mode-backwards'>
					<CardHeader>
						<CardTitle>Pending invites</CardTitle>
						<CardDescription>People who&apos;ve been invited but haven&apos;t joined yet.</CardDescription>
					</CardHeader>
					<CardContent>
						{pendingInvitations.length === 0 ? (
							<div
								className='flex flex-col items-center gap-2 rounded-lg border border-dashed py-10 text-center'
								data-testid='org-invitations-empty'
							>
								<div className='rounded-full bg-muted p-3 text-muted-foreground'>
									<Mail className='size-5' aria-hidden='true' />
								</div>
								<p className='text-sm font-medium'>No pending invites</p>
								<p className='text-xs text-muted-foreground max-w-sm'>
									Invites you send will appear here until they&apos;re accepted.
								</p>
							</div>
						) : (
							<div className='divide-y divide-border rounded-lg border overflow-hidden'>
								{pendingInvitations.map((inv) => (
									<div
										key={inv.id}
										className='group/inv flex items-center justify-between p-4 gap-4 transition-colors duration-150 hover:bg-muted/50'
									>
										<div className='flex items-center gap-3 min-w-0'>
											<div className='flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
												<Mail className='size-4' aria-hidden='true' />
											</div>
											<div className='min-w-0'>
												<p className='text-sm font-medium truncate'>{inv.email}</p>
												<p className='text-xs text-muted-foreground mt-1'>
													Permission: <span className='font-medium capitalize'>{inv.role}</span> · Expires:&nbsp;
													{new Date(inv.expiresAt).toLocaleDateString()}
												</p>
											</div>
										</div>
										{isAtLeastAdmin ? (
											<Button
												variant='outline'
												size='sm'
												className='text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive'
												onClick={() =>
													setConfirmState({
														open: true,
														kind: 'cancel-invitation',
														id: inv.id,
														label: inv.email,
													})
												}
												disabled={cancelInvitationMutation.isPending}
											>
												Cancel
											</Button>
										) : null}
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				<ConfirmDialog
					open={confirmState.open}
					onOpenChange={(open) => {
						if (!open) setConfirmState(CLOSE_CONFIRM);
					}}
					title={confirmState.kind === 'cancel-invitation' ? 'Cancel the invite?' : 'Remove from org?'}
					description={
						confirmState.kind === 'cancel-invitation'
							? `The invite to ${confirmState.label} will be revoked. They won't be able to join with that link again.`
							: `${confirmState.label} will be removed from this organization. They can be re-invited at any time.`
					}
					confirmText={confirmState.kind === 'cancel-invitation' ? 'Cancel invite' : 'Remove from org'}
					cancelText='Keep'
					onConfirm={handleConfirm}
					loading={confirmLoading}
					destructive
				/>
				<OrgInviteDialog open={isInviteOpen} onOpenChange={setIsInviteOpen} />
			</div>
		</div>
	);
}

function OrganizationLoading() {
	return (
		<div className='h-full flex-1 min-w-0 overflow-y-auto'>
			<div className='container mx-auto max-w-4xl p-6 space-y-8 pb-12 min-w-0'>
				<div className='space-y-2'>
					<Skeleton className='h-9 w-64' />
					<Skeleton className='h-4 w-96' />
				</div>
				<SkeletonCard />
				<SkeletonCard />
				<SkeletonCard />
			</div>
		</div>
	);
}

function SkeletonCard() {
	return (
		<Card className='shadow-sm'>
			<CardHeader>
				<Skeleton className='h-5 w-32' />
				<Skeleton className='h-4 w-64 mt-2' />
			</CardHeader>
			<CardContent className='space-y-3'>
				<Skeleton className='h-14 w-full' />
				<Skeleton className='h-14 w-full' />
				<Skeleton className='h-14 w-full' />
			</CardContent>
		</Card>
	);
}
